import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';

interface AUMConfirmationDialogProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: (aum: number) => void;
  snapshotAUM: number;
  currency?: string;
  isLoading?: boolean;
}

export const AUMConfirmationDialog: React.FC<AUMConfirmationDialogProps> = ({
  visible,
  onHide,
  onConfirm,
  snapshotAUM,
  currency = 'USD',
  isLoading = false,
}) => {
  const [aum, setAUM] = useState<number>(snapshotAUM);

  const handleConfirm = () => {
    onConfirm(aum);
  };

  const handleHide = () => {
    setAUM(snapshotAUM); // Reset to original value
    onHide();
  };

  const dialogFooter = (
    <div className="flex justify-content-end align-items-center gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleHide}
        severity="secondary"
        size="small"
        disabled={isLoading}
        style={{
          padding: '0.4375rem 0.65625rem',
        }}
      />
      <Button
        label="Confirm & Analyze"
        icon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
        onClick={handleConfirm}
        autoFocus
        size="small"
        loading={isLoading}
        disabled={isLoading}
        style={{
          padding: '0.4375rem 0.65625rem',
        }}
      />
    </div>
  );

  return (
    <Dialog
      header="Confirm AUM for Analysis"
      visible={visible}
      style={{ width: '400px' }}
      className="p-fluid"
      footer={dialogFooter}
      onHide={handleHide}
      pt={{
        header: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            color: 'var(--bq-text-primary)',
            padding: '1rem 1.5rem',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
          },
        },
        content: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            color: 'var(--bq-text-primary)',
          },
        },
        footer: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            borderTop: '1px solid var(--bq-border-muted)',
            padding: '1rem 1.5rem',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          },
        },
      }}
    >
      <div className="dark-form">
        <div
          style={{
            height: '1px',
            backgroundColor: 'var(--bq-border-muted)',
            margin: '0 -1.5rem',
            width: 'calc(100% + 3rem)',
          }}
        />

        <div className="grid pt-3">
          <div className="col-12">
            <div className="field">
              <label htmlFor="aum" className="font-bold block mb-2">
                AUM (Assets Under Management)
              </label>
              <InputNumber
                id="aum"
                value={aum}
                onValueChange={(e) => setAUM(e.value || 0)}
                mode="currency"
                currency={currency}
                locale="en-US"
                className="w-full"
                min={0}
                maxFractionDigits={0}
              />
              <small
                className="mt-1 block"
                style={{ color: 'var(--bq-text-secondary)' }}
              >
                Please confirm the AUM that will be used for the portfolio
                analysis.
              </small>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
